/**
 * Period preset options and date-range computation utilities.
 * Used by AdvancedFilterPanel and all CRM list pages.
 */

export const PERIOD_OPTIONS = [
  { value: 'today',         label: 'Today' },
  { value: 'this_week',     label: 'This Week' },
  { value: 'this_month',    label: 'This Month' },
  { value: 'last_month',    label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'this_year',     label: 'This Year' },
  { value: 'last_year',     label: 'Last Year' },
  { value: 'custom',        label: 'Custom Range' },
];

const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Returns { startDate, endDate } for a given period preset key.
 * Returns empty strings for 'custom' or unknown values.
 */
export function getPeriodDateRange(period) {
  const now = new Date();
  const today = fmt(now);

  switch (period) {
    case 'today':
      return { startDate: today, endDate: today };

    case 'this_week': {
      const d = new Date(now);
      const day = d.getDay(); // 0 = Sunday
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const monday = new Date(d);
      monday.setDate(d.getDate() + diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { startDate: fmt(monday), endDate: fmt(sunday) };
    }

    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0); // last day of current month
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end   = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    case 'last_3_months': {
      const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const end   = new Date(now.getFullYear(), now.getMonth(), 0); // last day of previous month
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    case 'last_6_months': {
      const start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
      const end   = new Date(now.getFullYear(), now.getMonth(), 0); // last day of previous month
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    case 'this_year': {
      const start = new Date(now.getFullYear(), 0, 1);
      const end   = new Date(now.getFullYear(), 11, 31); // December 31 of current year
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    case 'last_year': {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end   = new Date(now.getFullYear() - 1, 11, 31);
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    case 'last_3_days': {
      const start = new Date(now);
      start.setDate(now.getDate() - 2); // 3 days inclusive: day-2, day-1, today
      return { startDate: fmt(start), endDate: today };
    }

    case 'last_7_days': {
      const start = new Date(now);
      start.setDate(now.getDate() - 6); // 7 days inclusive
      return { startDate: fmt(start), endDate: today };
    }

    default:
      return { startDate: '', endDate: '' };
  }
}

/**
 * Returns the effective { startDate, endDate } for a filterData object.
 * - If period is a preset key → computed dates.
 * - If period is 'custom' or empty → use manual startDate/endDate from filterData.
 */
export function getActivePeriodRange(filterData) {
  if (filterData.period && filterData.period !== 'custom') {
    return getPeriodDateRange(filterData.period);
  }
  return {
    startDate: filterData.startDate || '',
    endDate:   filterData.endDate   || '',
  };
}

/**
 * Counts how many filters are actively applied.
 * @param {object} appliedFilters - the applied (committed) filter state
 * @param {Array}  filterFields   - field config array from AdvancedFilterPanel
 */
export function getActiveFilterCount(appliedFilters = {}, filterFields = []) {
  let count = 0;

  // Period counts as 1 if any date/period value is set
  if (appliedFilters.period || appliedFilters.startDate || appliedFilters.endDate) count++;

  filterFields.forEach((field) => {
    if (field.type === 'dateRange') {
      if (appliedFilters[field.fromKey] || appliedFilters[field.toKey]) count++;
    } else {
      const val = appliedFilters[field.key];
      if (val !== undefined && val !== null && val !== '' &&
          !(Array.isArray(val) && val.length === 0)) count++;
    }
  });

  return count;
}

/** Default empty period filter state to merge into a page's filterData */
export const DEFAULT_PERIOD_STATE = {
  period:    '',
  startDate: '',
  endDate:   '',
};
